import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Leaf, Loader2, LogOut, CalendarCheck, Eye, EyeOff, KeyRound, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'pp_token';

// Send the JWT as a Bearer token (primary) plus the cookie (fallback). The CDN
// rewrites auth cookies to SameSite=None; Partitioned, which browsers don't
// return reliably, so the Bearer header is what keeps the session working.
const authCfg = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export default function PractitionerPortal() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [changeOpen, setChangeOpen] = useState(false);
  const [changePw, setChangePw] = useState({ current_password: '', new_password: '' });
  const [changeBusy, setChangeBusy] = useState(false);

  const loadBookings = useCallback(() => {
    axios.get(`${API}/discovery-call/bookings`, authCfg()).then(r => setBookings(r.data)).catch(() => {});
  }, []);

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(null); setChecking(false); return; }
    try {
      const r = await axios.get(`${API}/staff/me`, authCfg());
      setUser(r.data);
      loadBookings();
    } catch { localStorage.removeItem(TOKEN_KEY); setUser(null); } finally { setChecking(false); }
  }, [loadBookings]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      if (r.data?.access_token) localStorage.setItem(TOKEN_KEY, r.data.access_token);
      await loadMe();
      toast.success('Signed in');
    } catch { toast.error('Invalid email or password'); } finally { setBusy(false); }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, authCfg()); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setForgotMsg('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail, origin_url: window.location.origin }, { withCredentials: true });
    } catch { /* ignore — same generic message either way */ }
    setForgotMsg('If an account exists for that email, a password reset link has been sent. Please check your inbox (and spam folder).');
    setBusy(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (changePw.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setChangeBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, changePw, authCfg());
      toast.success('Password updated — please sign in again');
      localStorage.removeItem(TOKEN_KEY);
      setChangeOpen(false);
      setChangePw({ current_password: '', new_password: '' });
      setUser(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally { setChangeBusy(false); }
  };

  const confirm = async (id) => {
    const f = form[id] || {};
    if (!f.confirmed_date || !f.confirmed_time) { toast.error('Set a date and time'); return; }
    try {
      await axios.patch(`${API}/discovery-call/bookings/${id}/confirm`, {
        confirmed_date: f.confirmed_date, confirmed_time: f.confirmed_time,
        meeting_link: f.meeting_link || '', notes: f.notes || '',
      }, authCfg());
      toast.success('Confirmed — client emailed');
      loadBookings();
    } catch { toast.error('Failed to confirm'); }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6" data-testid="practitioner-login">
        {view === 'login' ? (
          <form onSubmit={login} className="bg-white rounded-3xl shadow-soft border border-stone-200 p-8 w-full max-w-sm">
            <div className="flex items-center gap-2 text-emerald-800 font-bold mb-1"><Leaf className="w-5 h-5" /> Practitioner Portal</div>
            <p className="text-sm text-stone-500 mb-6">Discovery Call bookings</p>
            <input type="email" required placeholder="Email" value={creds.email} onChange={(e) => setCreds(s => ({ ...s, email: e.target.value }))} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-emerald-500" data-testid="pp-email" />
            <div className="relative mb-2">
              <input type={showPw ? 'text' : 'password'} required placeholder="Password" value={creds.password} onChange={(e) => setCreds(s => ({ ...s, password: e.target.value }))} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-emerald-500" data-testid="pp-password" />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-emerald-700" data-testid="pp-toggle-password">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end mb-4">
              <button type="button" onClick={() => { setView('forgot'); setForgotEmail(creds.email); setForgotMsg(''); }} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900" data-testid="pp-forgot-link">
                Forgot password?
              </button>
            </div>
            <button type="submit" disabled={busy} className="w-full bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pp-login-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={sendReset} className="bg-white rounded-3xl shadow-soft border border-stone-200 p-8 w-full max-w-sm" data-testid="pp-forgot-form">
            <div className="flex items-center gap-2 text-emerald-800 font-bold mb-1"><KeyRound className="w-5 h-5" /> Reset your password</div>
            <p className="text-sm text-stone-500 mb-6">Enter your email and we'll send you a secure link to set a new password.</p>
            <input type="email" required placeholder="Email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm mb-4 focus:outline-none focus:border-emerald-500" data-testid="pp-forgot-email" />
            {forgotMsg && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-4" data-testid="pp-forgot-msg">{forgotMsg}</p>}
            <button type="submit" disabled={busy} className="w-full bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pp-send-reset-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
            </button>
            <button type="button" onClick={() => { setView('login'); setForgotMsg(''); }} className="mt-3 w-full text-sm font-semibold text-stone-500 hover:text-emerald-800" data-testid="pp-back-to-login">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-24 px-6" data-testid="practitioner-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-bold text-emerald-950">Discovery Call Bookings</h1>
            <p className="text-sm text-stone-500">Signed in as {user.name || user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setChangeOpen(true); setChangePw({ current_password: '', new_password: '' }); }} className="inline-flex items-center gap-2 text-sm border border-stone-300 rounded-xl px-4 py-2.5 hover:bg-white" data-testid="pp-change-password-open"><KeyRound className="w-4 h-4" /> Change password</button>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm border border-stone-300 rounded-xl px-4 py-2.5 hover:bg-white" data-testid="pp-logout"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>

        {changeOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-emerald-950/40 px-6" data-testid="pp-change-modal">
            <form onSubmit={changePassword} className="bg-white rounded-2xl shadow-2xl border border-stone-200 p-6 w-full max-w-sm relative">
              <button type="button" onClick={() => setChangeOpen(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-700" aria-label="Close" data-testid="pp-change-close"><X className="w-5 h-5" /></button>
              <div className="flex items-center gap-2 text-emerald-800 font-bold mb-4"><KeyRound className="w-5 h-5" /> Change password</div>
              <input type="password" required placeholder="Current password" value={changePw.current_password} onChange={(e) => setChangePw(s => ({ ...s, current_password: e.target.value }))} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:border-emerald-500" data-testid="pp-change-current" />
              <input type={showPw ? 'text' : 'password'} required placeholder="New password (min 8 characters)" value={changePw.new_password} onChange={(e) => setChangePw(s => ({ ...s, new_password: e.target.value }))} className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm mb-2 focus:outline-none focus:border-emerald-500" data-testid="pp-change-new" />
              <label className="flex items-center gap-2 text-xs text-stone-500 mb-4 cursor-pointer">
                <input type="checkbox" checked={showPw} onChange={() => setShowPw(v => !v)} data-testid="pp-change-show" /> Show passwords
              </label>
              <button type="submit" disabled={changeBusy} className="w-full bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="pp-change-submit">
                {changeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
              </button>
            </form>
          </div>
        )}

        {user.must_change_password && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-6" data-testid="pp-password-nudge">
            <KeyRound className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-900 flex-1">For your security, please set your own password. You're currently using the one we sent you.</p>
            <button onClick={() => { setChangeOpen(true); setChangePw({ current_password: '', new_password: '' }); }} className="text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-4 py-2.5 whitespace-nowrap" data-testid="pp-nudge-set-password">Set my password</button>
          </div>
        )}


        {bookings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl"><CalendarCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" /><p className="text-stone-500">No booking requests yet.</p></div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b, i) => (
              <div key={b.id} className="bg-white rounded-2xl p-5 shadow-sm border border-stone-200" data-testid={`pp-booking-${i}`}>
                <p className="font-bold text-emerald-950">{b.full_name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span></p>
                <p className="text-sm text-stone-500">{b.email} · {b.phone} · {b.method === 'phone' ? 'Phone' : 'Video'}</p>
                <p className="text-xs text-stone-500 mt-1">Preferred: {b.preferred_date || '—'} {b.preferred_time}. {b.message ? `“${b.message}”` : ''}</p>
                {b.status === 'confirmed' ? (
                  <p className="text-xs text-emerald-700 mt-2">Confirmed for {b.confirmed_date} at {b.confirmed_time}{b.meeting_link ? ` · ${b.meeting_link}` : ''}</p>
                ) : (
                  <div className="grid sm:grid-cols-4 gap-2 items-end border-t border-stone-100 pt-3 mt-3">
                    <input type="date" onChange={(e) => setForm(s => ({ ...s, [b.id]: { ...s[b.id], confirmed_date: e.target.value } }))} className="border-2 border-stone-200 rounded-lg px-3 py-2 text-sm" data-testid={`pp-date-${i}`} />
                    <input type="time" onChange={(e) => setForm(s => ({ ...s, [b.id]: { ...s[b.id], confirmed_time: e.target.value } }))} className="border-2 border-stone-200 rounded-lg px-3 py-2 text-sm" data-testid={`pp-time-${i}`} />
                    <input placeholder="Zoom/Teams link" onChange={(e) => setForm(s => ({ ...s, [b.id]: { ...s[b.id], meeting_link: e.target.value } }))} className="border-2 border-stone-200 rounded-lg px-3 py-2 text-sm" data-testid={`pp-link-${i}`} />
                    <button onClick={() => confirm(b.id)} className="bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-2 rounded-lg text-sm" data-testid={`pp-confirm-${i}`}>Confirm &amp; Email</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
